import type { Enemy } from '../Enemy';
import type { MovementBehavior } from './MovementBehavior';
import { getEnemyMovementTopY, getEnemyMovementBottomY } from '../../constants/field';
import { isMobile } from '../../utils/platform';

// Замедление зайца на мобильном. JumpBehavior не читает config.speed (всегда 0
// у RABBIT_CONFIG), поэтому ENEMY_SPEED_MOBILE_MULTIPLIER на него не действует —
// нужна отдельная правка. Эффективная скорость = jumpDistanceX / (duration + cooldown):
// 80 / (500 + 200) = 114 px/s → 80 / (500 + 500) = 80 px/s, т.е. ~−30%.
// Увеличиваем именно cooldown (не duration): сама траектория прыжка остаётся
// прежней, но между прыжками заяц дольше "сидит" — визуально естественно.
const RABBIT_COOLDOWN_MOBILE_MULTIPLIER = 2.5;

interface JumpState {
  phase: 'jumping' | 'cooldown';
  phaseElapsedMs: number;
  jumpStartX: number;
  jumpStartY: number;
  jumpEndX: number;
  jumpEndY: number;
}

export class JumpBehavior implements MovementBehavior {
  private state!: JumpState;
  private jumpDistanceX = 80;
  private jumpYRange = 50;
  private jumpDurationMs = 500;
  private cooldownMs = 200;
  private arcHeight = 30;

  init(enemy: Enemy, params?: Record<string, number>): void {
    if (params) {
      this.jumpDistanceX = params.jumpDistanceX ?? this.jumpDistanceX;
      this.jumpYRange = params.jumpYRange ?? this.jumpYRange;
      this.jumpDurationMs = params.jumpDurationMs ?? this.jumpDurationMs;
      this.cooldownMs = params.cooldownMs ?? this.cooldownMs;
      this.arcHeight = params.arcHeight ?? this.arcHeight;
    }
    if (isMobile()) {
      this.cooldownMs *= RABBIT_COOLDOWN_MOBILE_MULTIPLIER;
    }
    this.startNewJump(enemy);
  }

  private startNewJump(enemy: Enemy): void {
    const yOffset = (Math.random() - 0.5) * 2 * this.jumpYRange;
    // Клампим конечную Y в границах поля. Учитываем arcHeight — дуга временно
    // поднимает зайца выше jumpEndY на arcHeight в момент пика.
    const halfSize = enemy.displayWidth / 2;
    const effectiveMinY = getEnemyMovementTopY(enemy.scene) + halfSize + this.arcHeight;
    const effectiveMaxY = getEnemyMovementBottomY(enemy.scene) - halfSize;
    const jumpEndY = Math.max(
      effectiveMinY,
      Math.min(effectiveMaxY, enemy.y + yOffset),
    );

    this.state = {
      phase: 'jumping',
      phaseElapsedMs: 0,
      jumpStartX: enemy.x,
      jumpStartY: enemy.y,
      jumpEndX: enemy.x + this.jumpDistanceX,
      jumpEndY,
    };
  }

  update(enemy: Enemy, deltaMs: number, _target: { x: number; y: number }): void {
    this.state.phaseElapsedMs += deltaMs;

    if (this.state.phase === 'jumping') {
      const t = Math.min(1, this.state.phaseElapsedMs / this.jumpDurationMs);
      const baseX = this.state.jumpStartX + (this.state.jumpEndX - this.state.jumpStartX) * t;
      const baseY = this.state.jumpStartY + (this.state.jumpEndY - this.state.jumpStartY) * t;
      const arcOffset = -Math.sin(Math.PI * t) * this.arcHeight;
      enemy.x = baseX;
      enemy.y = baseY + arcOffset;

      if (t >= 1) {
        enemy.x = this.state.jumpEndX;
        enemy.y = this.state.jumpEndY;
        this.state.phase = 'cooldown';
        this.state.phaseElapsedMs = 0;
      }
    } else {
      if (this.state.phaseElapsedMs >= this.cooldownMs) {
        this.startNewJump(enemy);
      }
    }
  }
}
